import React, { StrictMode, useState, Suspense, useContext } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { essentialLocales, setAppLocales } from '@fs/zion-locale'
import { ConditionalFooterContext } from '@fs/zion-record-search'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router, useLocation } from '@fs/zion-router'
import { sentryDSN, mergeSentryConfig, appPath } from '@fs/zion-config'
import { useFeatureFlag } from '@fs/zion-flags'

import { AppLayout } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { BrightspotProvider } from '@fs/zion-cms'
import App from './App'
import { SEARCH_CONTENT_ID } from './components/shared/SearchContentWrapper'
import * as serviceWorker from './serviceWorker'
import './locales'
import addServerExSupport from './lib/serverExSupport'
import DarkModeProvider, { DarkModeContext } from './DarkModeProvider'
import { BackNavigationProvider } from './components/film-viewer/BackNavigationProvider'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      denyUrls: [
        // Ignore errors from saved pages
        'file:///',
      ],
      ignoreErrors: [
        // This error happens on beta when we try to use a restricted digitally born image
        "getBornDigitalContent can't identify digital-content because we caught err",
        // Happens in WebKit, particularly iOS Mail. Unresolved WebKit issue: https://bugs.webkit.org/show_bug.cgi?id=197050
        /UnknownError.*Indexed Database server.*/,
        // This error happens when a browser aborts a request
        'Request aborted',
        // We have some off-page dependencies that seem to fail occasionally
        /(fbq|gtag) is not defined/,
        /Can't find variable: (fbq|gtag)/,
        // This should be fixed in zion-flags v2.1.1, but we are somehow still getting the errors
        /No split available for [a-zA-Z0-9_]+/,
        // Caused by TrustArc banner. Jacob Wilden is our contact with TrustArc. Benign error that we have no control over
        /Cannot read properties of undefined \(reading 'c'\)/,
        /undefined is not an object \(evaluating 'd\.c'\)/,
        /d is undefined/,
        /can't access property "c", n is undefined/,
        /undefined is not an object \(evaluating 'n\.c'\)/,
        /n is undefined/,
        // Outlook notification error, benign and non-impactful
        /Non-Error promise rejection captured with value: Object Not Found Matching Id:(\d+), MethodName:/,
        // possible relationship issue in the Family Tree
        /duplicatePersonId found on SL page/,
        /circularRelationships found on SL page/,
        // These are valid errors that we don't care about logging in Sentry since they are generally caused by restricted access to images or resources and are not actionable for us to fix
        'Request failed with status code 401',
        'Request failed with status code 403',
        'bad serialized structured data',
        'An attempt was made to use an object that is not, or is no longer, usable',
      ],
      integrations: [
        Sentry.captureConsoleIntegration({
          levels: ['error'],
        }),
        Sentry.browserTracingIntegration(),
        Sentry.extraErrorDataIntegration({
          // Limit of how deep the object serializer should go. Anything deeper than limit will
          // be replaced with standard Node.js REPL notation of [Object], [Array], [Function] or
          // a primitive value. Defaults to 3.
          // Changed from 4 to 3 to fix SEARCH-REACT-1CND: Firefox's stricter structured clone
          // algorithm fails when Sentry tries to serialize deeply nested objects with circular references
          depth: 3,
        }),
      ],
      sampleRate: 0.25,
      // This reduces how many transactions are sent to Sentry. search-react sends more transactions that other projects
      tracesSampleRate: 0.00005,
      // Before sending the event to Sentry, we want to filter out some events that we know are not actionable for us to fix, and are just noise in Sentry, but don't have a clear error message that we can filter on in ignoreErrors
      beforeSend(event) {
        // Filter out axios errors with status code 0 (cancelled/aborted requests)
        const isAxiosError = event.contexts?.AxiosError
        // eslint-disable-next-line no-underscore-dangle
        const statusCode = event.contexts?.AxiosError?.request?.__sentry_xhr_v3__?.status_code
        if (isAxiosError && statusCode === 0) {
          return null
        }

        // Filter TrustArc network errors
        const breadcrumbValues = Array.isArray(event.breadcrumbs?.values) ? event.breadcrumbs?.values : []
        const hasTrustArcError = breadcrumbValues.some(
          (breadcrumb) =>
            breadcrumb.category === 'fetch' &&
            breadcrumb.level === 'error' &&
            breadcrumb.data?.url?.includes('trustarc.com')
        )

        if (hasTrustArcError && event.exception?.values?.[0]?.value?.includes('NetworkError')) {
          return null
        }

        return event
      },
    })
  )
}

// Consistent scroll restoration behavior across browsers
window.history.scrollRestoration = 'auto'

const RootWrapper = () => {
  const { theme } = useContext(DarkModeContext)
  const [showFooter, setShowFooter] = useState(true)
  const { pathname } = useLocation()
  const isGenealogies = pathname.startsWith('/ark:/61903/2:')
  const [useFatFooter, setUseFatFooter] = useState(false)
  const showHelpChatbot = useFeatureFlag('search_showHelpChatbot')

  return (
    <ConditionalFooterContext.Provider value={setShowFooter}>
      <Root
        helpChatbot={showHelpChatbot.isOn}
        theme={theme}
        analytics
        fullScreen={isGenealogies}
        footer={{ hidden: !showFooter, useFatFooter }}
        skipLinkId={SEARCH_CONTENT_ID}
      >
        <AppLayout fullWidth>
          <App setUseFatFooter={setUseFatFooter} />
        </AppLayout>
      </Root>
    </ConditionalFooterContext.Provider>
  )
}

const handledExternalRoutes = [
  // These routes look really weird because they get added to a regex and compiled in react-router's matchPath() function
  //  - thus the double slashes and arkPath match that don't seem to make sense
  // It's important that the whole path is included here because the regex adds a check for /, ?, or end of URL after this string

  // handle 1:1: arks locally, always
  '/ark\\:/61903/1\\:1\\::arkPath',

  // handle 2: arks only if the React genealogies details page is released
  '/ark\\:/61903/2\\::arkPath',
]

const RouterWrapper = () => {
  const base = appPath ? new URL(appPath).pathname : '/'

  return (
    <Router basename={base} key={base} handledExternalRoutes={handledExternalRoutes}>
      <BackNavigationProvider>
        <DarkModeProvider>
          <RootWrapper />
        </DarkModeProvider>
      </BackNavigationProvider>
    </Router>
  )
}

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  return (
    <BrightspotProvider sites={['search-web']} hasCustomContent>
      <RouterWrapper />
    </BrightspotProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </StrictMode>
  )
}

// We need to be able to set server side experiment headers for requests that are made by the browser.
// This global intercept is designed for that specific purpose.
addServerExSupport(['dasProxy'], ['das.prod.proxy,dz.prod.proxy'])

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
